/**
 * Pacific Northwest and West Coast craft malthouses — Admiral Maltings,
 * Eckert Malting, LINC Malt, Mainstem Malt, Mecca Grade Estate Malt, Cold
 * Stream Malt and Grain, Skagit Valley Malting and Cascadia Malts.
 *
 * Mecca Grade `pelton` and `lamonta` keep their original ids. The product
 * details are corrected in place: Pelton is the pilsner-style foundation malt
 * and Lamonta is the American pale, not a Munich.
 */

import type { Fermentable } from '../../../schema/fermentable';
import { craftMalt } from './helpers';

/** California, Washington and Oregon craft malts. @see Fermentable */
export const westCoastCraftFermentables: readonly Fermentable[] = [
  // ---------------------------------------------------------------------------
  // Admiral Maltings — Alameda, California
  //
  // Out of business. Every product page under `admiralmaltings.com/news/malt/`
  // returned 404 in the October 2026 URL check, so the links were removed. The
  // rows stay for the same reason as Mecca Grade's below: old recipes and
  // cupboards still need them to resolve.
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'admiral-pils',
    name: 'Admiral Pils',
    maker: 'Admiral Maltings',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 1.6,
    diastaticPowerLintner: 85,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Floor-malted California pilsner from Sacramento Valley and Tule Lake barley. Bright straw wort with honeysuckle, vanilla and dry grass — the house lager canvas, also sold organic.',
    tags: ['base', 'pilsner', 'craft-malt', 'california', 'floor-malted', 'organic'],
  }),
  craftMalt({
    id: 'admiral-feldblume',
    name: 'Feldblume',
    maker: 'Admiral Maltings',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 2.4,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Light golden floor malt with clover, hay and a mild scone-like sweetness. A step richer than Admiral Pils, still pale enough for saison, kolsch and hop-forward pale beer.',
    tags: ['base', 'pale', 'craft-malt', 'california', 'floor-malted'],
  }),
  craftMalt({
    id: 'admiral-gallaghers-best',
    name: "Gallagher's Best",
    maker: 'Admiral Maltings',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 3.2,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Well-balanced pale ale malt: multigrain crackers, hay, and toast with honey. The English-leaning ale base from California floor-malted barley.',
    tags: ['base', 'pale-ale', 'craft-malt', 'california', 'floor-malted'],
  }),
  craftMalt({
    id: 'admiral-maiden-voyage',
    name: 'Maiden Voyage',
    maker: 'Admiral Maltings',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 3,
    diastaticPowerLintner: 75,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Organic biscuity pale with dry grass, toast with honey and peanut butter. CCOF-certified California grain, floor-malted in Alameda; a flavor-forward everyday ale malt.',
    tags: ['base', 'pale', 'craft-malt', 'california', 'floor-malted', 'organic'],
  }),
  craftMalt({
    id: 'admiral-pacific-victor',
    name: 'Pacific Victor',
    maker: 'Admiral Maltings',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 6,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Organic Vienna-leaning malt with a light orange hue, sweet tea, breakfast cereal, honey and almond. Self-converting as a whole grist or a 15–40 % seasoning.',
    tags: ['kilned', 'vienna', 'craft-malt', 'california', 'floor-malted', 'organic'],
  }),
  craftMalt({
    id: 'admiral-midway',
    name: 'Midway',
    maker: 'Admiral Maltings',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 12,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Organic amber malt with cinnamon, bread crust, graham cracker, toast and pretzel. The house Munich: rich color and fruity sweetness, still able to convert itself.',
    tags: ['kilned', 'munich', 'craft-malt', 'california', 'floor-malted', 'organic'],
  }),
  craftMalt({
    id: 'admiral-hearth',
    name: "Admiral's Hearth",
    maker: 'Admiral Maltings',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 76,
    moisturePercent: 4,
    colorLovibond: 18,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Organic dark Munich: warm amber color, bread crust, toffee and cake. Enzyme is limited, so keep a paler base alongside it past about 40 %.',
    tags: ['kilned', 'munich', 'craft-malt', 'california', 'floor-malted', 'organic'],
  }),
  craftMalt({
    id: 'admiral-kilnsmith',
    name: 'Kilnsmith',
    maker: 'Admiral Maltings',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 75,
    moisturePercent: 4,
    colorLovibond: 30,
    diastaticPowerLintner: 10,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'Organic melanoidin-style malt: ruby brown wort with Grape-Nuts, toffee and brown sugar. Five to fifteen percent in amber ales and malt-forward lagers; not a base malt.',
    tags: ['kilned', 'melanoidin', 'craft-malt', 'california', 'floor-malted', 'organic'],
  }),
  craftMalt({
    id: 'admiral-yolo-gold-wheat',
    name: 'Yolo Gold Wheat',
    maker: 'Admiral Maltings',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    colorLovibond: 2.5,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Floor-malted California wheat with hay, mineral and rising-dough notes. Pillowy mouthfeel for wheat beers and hazy IPA; rice hulls past about a third of the grist.',
    tags: ['base', 'wheat', 'craft-malt', 'california', 'floor-malted', 'head-retention'],
  }),
  craftMalt({
    id: 'admiral-clipper-oats',
    name: 'Clipper Oats',
    maker: 'Admiral Maltings',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 76,
    moisturePercent: 6,
    colorLovibond: 3,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Floor-malted California oats: cake, chocolate, hay, pecan, clover and honeysuckle. Creamy weight for hazy IPA and stout with less gumminess than flakes.',
    tags: ['adjunct', 'oats', 'craft-malt', 'california', 'floor-malted', 'mouthfeel'],
  }),
  craftMalt({
    id: 'admiral-capay-rye',
    name: 'Capay Rye',
    maker: 'Admiral Maltings',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 4,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Floor-malted Capay Valley rye: bright amber hue, spicy Grape-Nuts and almond sugar-cookie sweetness. Sticky mash above about 20 %; hulls are part of the recipe.',
    tags: ['base', 'rye', 'craft-malt', 'california', 'floor-malted', 'spicy'],
  }),
  craftMalt({
    id: 'admiral-its-the-chit',
    name: "It's the Chit!",
    maker: 'Admiral Maltings',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 72,
    moisturePercent: 7,
    colorLovibond: 1.8,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'Organic undermodified chit malt for foam, body and haze rather than flavor. Green and grainy; use in place of raw grain or dextrin malt at 5–15 %.',
    tags: ['base', 'chit', 'craft-malt', 'california', 'floor-malted', 'organic', 'body', 'haze'],
  }),

  // ---------------------------------------------------------------------------
  // Eckert Malting — Chico, California
  //
  // **Extract is rice extract, not barley extract.** These rows used to carry
  // 66–80 % because they were filled in from barley class-typicals. Eckert malt
  // rice with the hull on, roughly 62.5 % grain to 37.5 % hull, and the hull
  // yields nothing, so the whole line sits well below any barley malt.
  //
  // Picking the figure needs care, because the two published numbers measure
  // different things. Gluten Free Home Brewing (Eckert's exclusive US
  // distributor) print "20–22 gravity points per gallon; or with Ondea Pro
  // approximately 28–30" on every SKU. The 20–22 is a *practical* yield with
  // rice malt's own feeble enzyme, and this field is a *lab* extract — the
  // engine derates for mash efficiency separately in `fermentablePpg`, so
  // storing a practical figure would derate it twice.
  //
  // The potential figure is measured rather than inferred: Otherwise Brewing ran
  // a four-hour Termamyl rest on pale rice malt to a kettle gravity of 1.067,
  // which puts its potential at 28–30 PPG. At 4.5 % moisture that is 63–68 %
  // FGDB, so pale rice is 65 (28.7 PPG) and the rest of the line keeps Eckert's
  // own ordering, scaled by the same factor.
  //
  // DP on the pale drops 35 → 22, which is Eckert's own "average range 20–25".
  // That is *below* self-conversion, which is the point: rice malt needs
  // exogenous enzyme, which is why the distributor quotes an Ondea Pro figure at
  // all. Extract and moisture stay class-typical and `representative`.
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'eckert-pale-rice',
    name: 'Pale Rice Malt',
    maker: 'Eckert Malting',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 65,
    moisturePercent: 6,
    colorLovibond: 1.4,
    diastaticPowerLintner: 22,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'California rice malted in a dedicated gluten-free house. The pale base for gluten-free lager and ale: grainy, bready, and more flavor than flaked rice without a cereal mash.',
    tags: ['adjunct', 'rice', 'gluten-free', 'craft-malt', 'california', 'base'],
  }),
  craftMalt({
    id: 'eckert-biscuit-rice',
    name: 'Biscuit Rice Malt',
    maker: 'Eckert Malting',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 63,
    moisturePercent: 5,
    colorLovibond: 4.8,
    diastaticPowerLintner: 15,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Lightly kilned rice malt at under 5 °L: dry biscuit, cracker and light toast, with no sweetness and no roast behind it. Eckert put the range at 5–60 %, so it can be most of a gluten-free amber grist rather than a seasoning.',
    tags: ['kilned', 'biscuit', 'rice', 'gluten-free', 'craft-malt', 'california'],
  }),
  craftMalt({
    id: 'eckert-amber-rice',
    name: 'Amber Rice Malt',
    maker: 'Eckert Malting',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 62,
    moisturePercent: 5,
    colorLovibond: 15,
    diastaticPowerLintner: 10,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'Amber-kilned California rice at about 15 °L: gentle toast and a balanced sweetness that reads closer to a soft crystal than to a kiln malt. One to twenty percent gives a gluten-free amber or red its color without barley Munich.',
    tags: ['kilned', 'amber', 'rice', 'gluten-free', 'craft-malt', 'california'],
  }),
  craftMalt({
    id: 'eckert-crystal-rice',
    name: 'Crystal Rice Malt',
    maker: 'Eckert Malting',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 60,
    moisturePercent: 6,
    colorLovibond: 16,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Stewed rice malt at 16 °L: caramel and sweet biscuit with prune and fig behind them, and a light reddish cast. The gluten-free crystal — three to fifteen percent builds body, foam and residual sweetness in ambers, browns and porters.',
    tags: ['crystal', 'caramel', 'rice', 'gluten-free', 'craft-malt', 'california'],
  }),
  // Eckert publish no figures, so the 22 °L comes from Gluten Free Home Brewing,
  // their exclusive US distributor, and their copy agrees with it: "toasted
  // bread, nutty notes, moderate color", "without harsh bitterness". We carried
  // 200 °L before, which nothing supports.
  //
  // Their 1–5% usage rate is *not* carried over. GFHB print that same rate on Gas
  // Hog (350–400 °L) and Pitch (400+ °L), alongside a DP and gravity block
  // identical on every Eckert SKU, so it is boilerplate from the roast group. A
  // 22 °L malt held to 5% would colour a beer by half a shade, which cannot be
  // what "best suited to brown ales and porters" means. Capped like the toasted
  // malt it is.
  craftMalt({
    id: 'eckert-james-brown-rice',
    name: "James' Brown Rice Malt",
    maker: 'Eckert Malting',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 61,
    moisturePercent: 4.5,
    colorLovibond: 22,
    diastaticPowerLintner: 10,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'Toasted bread and nut over a little caramelized grain, with none of the bitterness a dark rice malt carries. At 22 °L it colors gently, so a gluten-free brown ale wants a real proportion of it rather than a token handful.',
    tags: ['kilned', 'toasted', 'brown', 'rice', 'gluten-free', 'craft-malt', 'california'],
  }),
  craftMalt({
    id: 'eckert-dark-rice',
    name: 'Dark Rice Malt',
    maker: 'Eckert Malting',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 55,
    moisturePercent: 4,
    colorLovibond: 275,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    description:
      'Rice roasted to chocolate color, published at 250–300 °L: bold roast with caramelized grain underneath it. One to five percent is the whole job in a gluten-free porter or stout.',
    tags: ['roasted', 'chocolate', 'rice', 'gluten-free', 'craft-malt', 'california'],
  }),
  craftMalt({
    id: 'eckert-biscuit-4l-rice',
    name: 'Biscuit 4L Rice Malt',
    maker: 'Eckert Malting',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 63,
    moisturePercent: 5,
    colorLovibond: 4.3,
    diastaticPowerLintner: 15,
    suggestedMaxPercent: 80,
    needsMashing: true,
    description:
      'The restrained biscuit: even toasted-bread and cracker notes with barely any color, so it shapes the malt profile without sweetening or darkening the beer. Eckert take it as high as 80 %, which makes it a working base for gluten-free pale ales, IPAs and farmhouse beer as much as a seasoning.',
    tags: ['kilned', 'biscuit', 'rice', 'gluten-free', 'craft-malt', 'california'],
  }),
  craftMalt({
    id: 'eckert-biscuit-15l-rice',
    name: 'Biscuit 15L Rice Malt',
    maker: 'Eckert Malting',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 62,
    moisturePercent: 5,
    colorLovibond: 15.5,
    diastaticPowerLintner: 10,
    suggestedMaxPercent: 10,
    needsMashing: true,
    description:
      'A deeper cut of the same kiln: bread crust and cracker with real color behind it, still dry rather than sweet. Two to ten percent is where a gluten-free amber or darker lager gets its malt backbone.',
    tags: ['kilned', 'biscuit', 'rice', 'gluten-free', 'craft-malt', 'california'],
  }),
  craftMalt({
    id: 'eckert-biscuit-18l-rice',
    name: 'Biscuit 18L Rice Malt',
    maker: 'Eckert Malting',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 62,
    moisturePercent: 5,
    colorLovibond: 18,
    diastaticPowerLintner: 8,
    suggestedMaxPercent: 10,
    needsMashing: true,
    description:
      'The darkest of the three biscuits: pronounced toast and bread crust, noticeable amber depth, and a finish that stays clean and dry. Two to ten percent in a malt-forward gluten-free beer that wants structure rather than sweetness.',
    tags: ['kilned', 'biscuit', 'rice', 'gluten-free', 'craft-malt', 'california'],
  }),
  craftMalt({
    id: 'eckert-gas-hog-rice',
    name: 'Gas Hog Rice Malt',
    maker: 'Eckert Malting',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 55,
    moisturePercent: 4,
    colorLovibond: 375,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    description:
      'Rice roasted hard, to 350–400 °L: strong roast with dark caramelized notes and a firm malt backbone under them. One to five percent is the ceiling — this is the malt a gluten-free imperial stout is colored and flavored with.',
    tags: ['roasted', 'dark', 'rice', 'gluten-free', 'craft-malt', 'california', 'stout'],
  }),
  craftMalt({
    id: 'eckert-pitch-rice',
    name: 'Pitch Rice Malt',
    maker: 'Eckert Malting',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 54,
    moisturePercent: 4,
    colorLovibond: 450,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    description:
      'The end of the Eckert roast at 400 °L and up: almost black, sharp, with a burnt-sugar edge. Bought for color rather than flavor, so a percent or two is usually all a gluten-free stout or black IPA wants from it.',
    tags: ['roasted', 'black', 'rice', 'gluten-free', 'craft-malt', 'california', 'colour'],
  }),

  // ---------------------------------------------------------------------------
  // LINC Malt — Spokane Valley, Washington
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'linc-genie-pale',
    name: 'Genie Pale',
    maker: 'LINC Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 83,
    moisturePercent: 4.5,
    colorLovibond: 1.9,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'House 2-row from Salmon-Safe Inland Northwest barley. Clean cracker and biscuit; high extract for a craft malt and the cooperative’s all-performance pale base.',
    tags: ['base', 'pale', 'craft-malt', 'washington', 'genie', 'cooperative'],
  }),
  craftMalt({
    id: 'linc-baronesse-pale',
    name: 'Baronesse Pale',
    maker: 'LINC Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.5,
    moisturePercent: 4.5,
    colorLovibond: 2.4,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 100,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Northwest-style pale from Colfax-grown Baronesse: earth, hay and honeysuckle. A Craft Malt Cup medalist, richer than the Genie house 2-row.',
    tags: ['base', 'pale', 'craft-malt', 'washington', 'cooperative'],
  }),
  craftMalt({
    id: 'linc-cashup-wheat',
    name: 'Cashup Wheat',
    maker: 'LINC Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 83,
    moisturePercent: 5,
    colorLovibond: 2.2,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 60,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Soft wheat from Joseph’s Grainery in Colfax. Excellent mouthfeel and a rich wheat flavor for weissbier, wit and hazy IPA; rice hulls at high rates.',
    tags: ['base', 'wheat', 'craft-malt', 'washington', 'cooperative', 'head-retention'],
  }),
  craftMalt({
    id: 'linc-99-triticale-pilsner',
    name: '99 Triticale Pilsner',
    maker: 'LINC Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 83.5,
    moisturePercent: 5,
    colorLovibond: 3.7,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 40,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'The lightest triticale malt in the LINC book: a rye–wheat hybrid, sweet and pale. Ten to thirty percent in farmhouse ales for spice and wheat silk without a full rye mash.',
    tags: ['base', 'triticale', 'pilsner', 'craft-malt', 'washington', 'cooperative'],
  }),
  craftMalt({
    id: 'linc-gazelle-rye',
    name: 'Gazelle Rye',
    maker: 'LINC Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    colorLovibond: 4.5,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 50,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Peppery cracker rye from Ritzville, one of the few ryes grown in the dryland Inland Northwest. Sticky mash above about 20 %; hulls are not optional at rye-beer rates.',
    tags: ['base', 'rye', 'craft-malt', 'washington', 'cooperative', 'spicy'],
  }),
  craftMalt({
    id: 'linc-red-russian-wheat',
    name: 'Red Russian Wheat',
    maker: 'LINC Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82,
    moisturePercent: 5,
    colorLovibond: 2.8,
    diastaticPowerLintner: 65,
    suggestedMaxPercent: 60,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Heritage hard red wheat from Palouse Heritage, grown without glyphosate. Red-berry notes and a rich wheat profile, exceptional in German-style wheat beers.',
    tags: ['base', 'wheat', 'heritage', 'craft-malt', 'washington', 'cooperative'],
  }),
  craftMalt({
    id: 'linc-sonora-white-wheat',
    name: 'Sonora White Wheat',
    maker: 'LINC Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 77.5,
    moisturePercent: 5,
    colorLovibond: 1.6,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 50,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Heritage Sonora white wheat: cream-of-wheat flavor, very pale, high protein. Built for hazy IPA and pale wheat beers; grown without glyphosate on the Palouse.',
    tags: ['base', 'wheat', 'heritage', 'craft-malt', 'washington', 'cooperative', 'haze'],
  }),

  // ---------------------------------------------------------------------------
  // Mainstem Malt — Walla Walla, Washington
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'mainstem-genie-pilsner',
    name: 'Genie Pilsner',
    maker: 'Mainstem Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 1.7,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Dryland Genie 2-row from Walla Walla-area farms, kilned as a pilsner. Conservation-focused malting: the grain is a rotational crop on farms that would otherwise fallow.',
    tags: ['base', 'pilsner', 'craft-malt', 'washington', 'genie', 'dryland'],
  }),
  craftMalt({
    id: 'mainstem-genie-pale',
    name: 'Genie Pale',
    maker: 'Mainstem Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 2.3,
    diastaticPowerLintner: 85,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'The same dryland Genie barley, kilned as a pale ale malt. Clean cracker and light toast; the everyday ale base from Walla Walla conservation acres.',
    tags: ['base', 'pale', 'craft-malt', 'washington', 'genie', 'dryland'],
  }),
  craftMalt({
    id: 'mainstem-soft-white-pale',
    name: 'Soft White Pale',
    maker: 'Mainstem Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    colorLovibond: 2.2,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Dryland soft white wheat malted in Walla Walla. Soft body and lasting foam for wheat beers and hazy ales; rice hulls at high rates.',
    tags: ['base', 'wheat', 'craft-malt', 'washington', 'dryland', 'head-retention'],
  }),
  craftMalt({
    id: 'mainstem-alder-smoked-vienna',
    name: 'Alder Smoked Vienna',
    maker: 'Mainstem Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 6,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Dryland Vienna smoked over alder. Sweeter and less phenolic than beech; can carry a smoked lager or season a porter at 5–20 %.',
    tags: ['smoked', 'alder', 'vienna', 'craft-malt', 'washington', 'dryland', 'base'],
  }),
  craftMalt({
    id: 'mainstem-hard-red-light-munich',
    name: 'Hard Red Light Munich',
    maker: 'Mainstem Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 8,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Hard red wheat kilned on a Munich schedule. Bread-crust depth with wheat silk — a distinctive 10–30 % seasoning in amber ales and darker wheat beers.',
    tags: ['kilned', 'munich', 'wheat', 'craft-malt', 'washington', 'dryland'],
  }),

  // ---------------------------------------------------------------------------
  // Mecca Grade Estate Malt — Madras, Oregon
  //
  // **Almost certainly out of production.** Mecca Grade was listed for sale in
  // March 2023 at $5.5m and sold on 1 July 2025 for $3m, with the new owners
  // stated as ceasing production; the malthouse ran a 35 %-off close-out on full
  // pallets before shutting. `meccagrade.com` now returns Squarespace's "Website
  // Expired" page, which is consistent. The reporting is New School Beer's rather
  // than an announcement from the company, so this is well-sourced but not
  // first-party.
  //
  // All eleven rows stay. A brewer with Mecca Grade in the cupboard or in an old
  // recipe still needs the numbers to resolve, and the specs below are drawn from
  // their own archived spec directory. Vanishing is not the same as never having
  // existed. If a discontinued marker ever lands in the schema, this maker is the
  // first customer for it.
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'mecca-grade-pelton',
    name: 'Pelton',
    maker: 'Mecca Grade Estate Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 1.5,
    diastaticPowerLintner: 100,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Pilsner-style foundation malt grown, malted and kilned on one central Oregon farm. Complex, slightly grassy and crisp, with enough enzyme to carry a whole lager or farmhouse grist.',
    tags: ['base', 'pilsner', 'craft-malt', 'oregon', 'estate', 'single-farm'],
  }),
  craftMalt({
    id: 'mecca-grade-lamonta',
    name: 'Lamonta',
    maker: 'Mecca Grade Estate Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 2.8,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'American pale from the same estate barley as Pelton: golden, sweet and slightly biscuity. High diastatic power for a craft malt and clean enough to carry pale ale, IPA or British-style ale.',
    tags: ['base', 'ale', 'pale', 'craft-malt', 'oregon', 'estate', 'single-farm'],
  }),
  craftMalt({
    id: 'mecca-grade-vanora',
    name: 'Vanora',
    maker: 'Mecca Grade Estate Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 4.5,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Estate Vienna-style malt, aromatically rich with a hint of walnut and a deep gold hue. Self-converting as a whole grist or a 15–40 % seasoning.',
    tags: ['kilned', 'vienna', 'craft-malt', 'oregon', 'estate'],
  }),
  craftMalt({
    id: 'mecca-grade-metolius',
    name: 'Metolius',
    maker: 'Mecca Grade Estate Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 10,
    diastaticPowerLintner: 45,
    suggestedMaxPercent: 80,
    needsMashing: true,
    description:
      'Estate Munich: toasted graham cracker with deep orange and brown color. Still comfortably self-converting; the malt depth in amber lager and darker ales.',
    tags: ['kilned', 'munich', 'craft-malt', 'oregon', 'estate'],
  }),
  craftMalt({
    id: 'mecca-grade-wickiup',
    name: 'Wickiup',
    maker: 'Mecca Grade Estate Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    colorLovibond: 3,
    diastaticPowerLintner: 65,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Hard red spring wheat grown on the Madras farm. Cream of Wheat flavor with bread crust and brown sugar; builds foam and body in wheat beers and hazy ales.',
    tags: ['base', 'wheat', 'craft-malt', 'oregon', 'estate', 'head-retention'],
  }),
  craftMalt({
    id: 'mecca-grade-shaniko',
    name: 'Shaniko',
    maker: 'Mecca Grade Estate Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82,
    moisturePercent: 5,
    colorLovibond: 2.2,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Soft white winter wheat from the estate. Cookie-dough sweetness and a pale wort — the softer wheat sibling of Wickiup, for wit, hefeweizen and hazy IPA.',
    tags: ['base', 'wheat', 'craft-malt', 'oregon', 'estate', 'head-retention'],
  }),
  craftMalt({
    id: 'mecca-grade-rimrock',
    name: 'Rimrock',
    maker: 'Mecca Grade Estate Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    // Mecca Grade publish 5.8 SRM: this is their Vienna-style rye, not a pale one.
    colorLovibond: 5.8,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Heirloom spring rye kilned to a rich orange hue: warm bread with graham and honey, and a herbal lift rather than the usual rye pepper. Sticky mash above about 20 %; hulls keep the lauter moving.',
    tags: ['base', 'rye', 'heirloom', 'vienna', 'craft-malt', 'oregon', 'estate'],
  }),
  craftMalt({
    id: 'mecca-grade-kaskela',
    name: 'Kaskela',
    maker: 'Mecca Grade Estate Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 79,
    moisturePercent: 5,
    colorLovibond: 3.2,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'The other estate rye, and the pale one: heirloom spring rye germinated slowly through the high-desert autumn and dried on moving air rather than in a hot kiln, which is what keeps its floral character intact. Rimrock is the same grain taken to a Vienna color, so reach for Kaskela when the rye should stay bright. Sticky mash above about 20 %.',
    tags: ['base', 'rye', 'heirloom', 'wind-malt', 'craft-malt', 'oregon', 'estate'],
  }),
  craftMalt({
    id: 'mecca-grade-opal-44',
    name: 'Opal 44',
    maker: 'Mecca Grade Estate Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 5,
    colorLovibond: 44,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Caramelized toffee malt from estate barley: sweet, moderately toasted, with hints of chocolate. Five to ten percent in amber ales and anything that wants toffee without a dark crystal.',
    tags: ['crystal', 'caramel', 'toffee', 'craft-malt', 'oregon', 'estate'],
  }),
  craftMalt({
    id: 'mecca-grade-gateway',
    name: 'Gateway',
    maker: 'Mecca Grade Estate Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 74,
    moisturePercent: 5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Wind-malt: dried on moving air rather than in a heated kiln, and stopped deliberately under-modified. Very pale and raw-grainy, with the protein still largely intact — a body and foam malt that also brings a distinctly rustic flavor.',
    tags: ['base', 'under-modified', 'wind-malt', 'craft-malt', 'oregon', 'estate'],
  }),
  craftMalt({
    id: 'mecca-grade-opal-22',
    name: 'Opal 22',
    maker: 'Mecca Grade Estate Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    colorLovibond: 22,
    extractFineDryPercent: 76,
    moisturePercent: 5.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'The lighter of the two Opal crystals: graham cracker and cocoa rather than the toasted toffee of Opal 44. Light enough to sweeten and round a pale beer without taking it amber.',
    tags: ['crystal', 'caramel', 'graham', 'cocoa', 'craft-malt', 'oregon', 'estate'],
  }),
  // ---------------------------------------------------------------------------
  // Cold Stream Malt & Grain Co. — Garfield, Washington
  //
  // A joint brand of two farms, Clearwater Farms (Nezperce, Idaho) and
  // Horlacher Farms (Latah, Washington), per coldstreammalt.com. The Craft
  // Maltsters Guild lists the Idaho partner as "Clearwater Farms"; that is
  // these malts, not a second maltster.
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'coldstream-brewer-select-blend',
    name: 'Brewer Select Blend',
    maker: 'Cold Stream Malt and Grain',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80.0,
    moisturePercent: 4.5,
    colorLovibond: 1.7,
    diastaticPowerLintner: 110,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A proprietary pilsner blend drawn from the farms’ best-performing lots, aimed at brewers who want extract and flavor to land in the same place batch after batch. White bread and light biscuit, Salmon Safe certified. Cold Stream publishes extract yield and color only; the moisture and diastatic figures here are base-malt typicals.',
    tags: ['base', 'pilsner', 'blend', 'washington', 'salmon-safe'],
  }),
  craftMalt({
    id: 'coldstream-buzz-pale',
    name: 'Buzz Pale',
    maker: 'Cold Stream Malt and Grain',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.0,
    moisturePercent: 4.5,
    colorLovibond: 1.9,
    diastaticPowerLintner: 110,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Pale malt from Buzz, a barley released by Montana State University’s breeding program, grown dryland in the Palouse. Subtle malty note over a clean, unfussy sensory profile. Cold Stream publishes extract yield and color only; the moisture and diastatic figures here are base-malt typicals.',
    tags: ['base', 'pale', 'buzz', 'palouse', 'dryland'],
  }),
  craftMalt({
    id: 'coldstream-buzz-pilsner',
    name: 'Buzz Pilsner',
    maker: 'Cold Stream Malt and Grain',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80.0,
    moisturePercent: 4.5,
    colorLovibond: 1.7,
    diastaticPowerLintner: 115,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'North American pilsner malt off the same Montana State Buzz variety, kilned light for clean flavor and straightforward brewhouse behavior. Cold Stream publishes extract yield and color only; the moisture and diastatic figures here are base-malt typicals.',
    tags: ['base', 'pilsner', 'buzz', 'palouse', 'lager'],
  }),
  craftMalt({
    id: 'coldstream-francin-pilsner',
    name: 'Francin Pilsner',
    maker: 'Cold Stream Malt and Grain',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80.0,
    moisturePercent: 4.5,
    colorLovibond: 1.65,
    diastaticPowerLintner: 110,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Czech barley newly introduced to the Pacific Northwest, malted to a pale pilsner spec. Floral aroma and a crisp palate that reads as Bohemian in origin. Cold Stream publishes extract yield and color only; the moisture and diastatic figures here are base-malt typicals.',
    tags: ['base', 'pilsner', 'czech', 'francin', 'palouse'],
  }),
  craftMalt({
    id: 'coldstream-genie-pale',
    name: 'Genie Pale',
    maker: 'Cold Stream Malt and Grain',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.0,
    moisturePercent: 4.5,
    colorLovibond: 2.0,
    diastaticPowerLintner: 110,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'All-round base malt from Genie barley with clean cracker and biscuit notes — the workhorse of the Cold Stream range. Cold Stream publishes extract yield and color only; the moisture and diastatic figures here are base-malt typicals.',
    tags: ['base', 'pale', 'genie', 'palouse', 'dryland'],
  }),
  craftMalt({
    id: 'coldstream-genie-pilsner',
    name: 'Genie Pilsner',
    maker: 'Cold Stream Malt and Grain',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80.0,
    moisturePercent: 4.5,
    colorLovibond: 1.7,
    diastaticPowerLintner: 115,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Genie barley taken to a North American pilsner spec: high extract, clean flavor and easy brewhouse performance. Cold Stream publishes extract yield and color only; the moisture and diastatic figures here are base-malt typicals.',
    tags: ['base', 'pilsner', 'genie', 'palouse', 'lager'],
  }),
  craftMalt({
    id: 'coldstream-steffi-pilsner',
    name: 'Steffi Pilsner',
    maker: 'Cold Stream Malt and Grain',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79.0,
    moisturePercent: 4.5,
    colorLovibond: 1.65,
    diastaticPowerLintner: 110,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Steffi is a Bavarian barley released in 1989 and still favoured by German maltsters. Light, straw-toned character suited to continental lagers. Cold Stream publishes extract yield and color only; the moisture and diastatic figures here are base-malt typicals.',
    tags: ['base', 'pilsner', 'steffi', 'bavarian', 'lager'],
  }),
  craftMalt({
    id: 'coldstream-stingray-wheat-pale',
    name: 'Stingray Wheat Pale',
    maker: 'Cold Stream Malt and Grain',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 84.1,
    moisturePercent: 4.5,
    colorLovibond: 2.0,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Pale wheat malt at 12.6 per cent protein, giving soft mouthfeel, light dough flavor and useful haze stability. Cold Stream publish an 84.1 per cent yield and 2.0 °L color; moisture and diastatic power here are wheat-malt typicals.',
    tags: ['wheat', 'base', 'stingray', 'palouse', 'haze'],
  }),

  // ---------------------------------------------------------------------------
  // Skagit Valley Malting — Burlington, Washington
  //
  // Founded 2011. All barley is grown in Skagit County, west of the Cascades.
  // The store (`skagitvalleymalting.com/collections/all-malts`) sells six
  // malts. Product pages carry almost no data: Pilsner says 1–2 °L, Pale says
  // 2–3 °L, and the other four leave the colour field empty.
  //
  // What they do publish is better than a spec sheet. The "Batch Analysis &
  // COA" page links a public Google Sheet with one row per 20,000 lb batch,
  // analysed by Montana State University: moisture, fine and coarse extract
  // (DB), colour in °SRM, β-glucan, protein, FAN, DP, alpha amylase and wort
  // pH. Pilsner, Pale, Vienna and British below are the **mean of every lot on
  // that sheet** as read on 2026-09-27. That is six Pilsner lots (five recorded
  // under the "Pilot" varietal and one under "Talisman"), three Pale lots, and
  // one lot each of Vienna and British. Those four rows are `spec-sheet`. The
  // sheet reports colour in °SRM, and for malt that is the same ASBC number as
  // °L, so it carries across unchanged (see the Blacklands note).
  //
  // The sheet also gives congress-wort pH (5.9–6.0). We leave `diPh` null
  // anyway: it is one lab's wort pH on single lots, not a published DI mash
  // pH, and the header comment in helpers.ts explains why we never guess one.
  //
  // Munich and Millet have no lot on the sheet and no colour on their pages,
  // so those two rows are class-typical and say so. The sheet's inventory tab
  // also lists lots the store does not sell (Whidbey NZ Pilsner, Planet Pale,
  // Francin Bohemian, Peat Smoked, Millet C40). Those are left out until they
  // appear as products. Francin Bohemian and Peat Smoked are graded "Bad" and
  // "Bad Dog" on the sheet.
  //
  // `url` points at the maker's own Shopify product page. Each malt appears
  // twice in the store; the handle used here is the one the all-malts
  // collection links to.
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'skagit-pilsner',
    name: 'Pilsner Malt',
    maker: 'Skagit Valley Malting',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 82,
    moisturePercent: 4.6,
    colorLovibond: 1.8,
    diastaticPowerLintner: 121,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Pale pilsner malt from barley grown in Skagit County, west of the Cascades. Their batch analyses run close to 1.8 °L with around 120 °Lintner, so it will carry an all-malt lager, kolsch or pale ale grist on its own with enzyme to spare.',
    tags: ['base', 'pilsner', 'lager', 'craft-malt', 'washington', 'local'],
    url: 'https://skagitvalleymalting.com/products/pilsner-malt',
    provenance: 'spec-sheet',
  }),
  craftMalt({
    id: 'skagit-pale',
    name: 'Pale Malt',
    maker: 'Skagit Valley Malting',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81.1,
    moisturePercent: 4.6,
    colorLovibond: 2.2,
    diastaticPowerLintner: 111,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Their pale ale base from Skagit County barley, a shade darker than their pilsner at about 2.2 °L. A whole-grist malt for pale ale and IPA, with plenty of enzyme for adjuncts.',
    tags: ['base', 'pale', 'ale', 'craft-malt', 'washington', 'local'],
    url: 'https://skagitvalleymalting.com/products/pale-malt',
    provenance: 'spec-sheet',
  }),
  craftMalt({
    id: 'skagit-british',
    name: 'British Malt',
    maker: 'Skagit Valley Malting',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 83.1,
    moisturePercent: 4.9,
    colorLovibond: 3.8,
    diastaticPowerLintner: 86,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'An English-style ale malt kilned darker than their pale, near 3.8 °L, for a fuller biscuit and bread backbone. Use it as the whole base of a bitter, ESB or mild.',
    tags: ['base', 'pale-ale', 'english', 'craft-malt', 'washington', 'local'],
    url: 'https://skagitvalleymalting.com/products/true-british-malt',
    provenance: 'spec-sheet',
  }),
  craftMalt({
    id: 'skagit-vienna',
    name: 'Vienna Malt',
    maker: 'Skagit Valley Malting',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80.8,
    moisturePercent: 3.8,
    colorLovibond: 3.8,
    diastaticPowerLintner: 104,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A light Vienna from Skagit County barley. The one analysed lot came in at 3.8 °L and over 100 °Lintner, so it can be the whole grist of a Vienna lager or amber, or 20 to 40 percent of a pale beer for more malt depth.',
    tags: ['kilned', 'vienna', 'craft-malt', 'washington', 'local'],
    url: 'https://skagitvalleymalting.com/products/vienna-malt-1',
    provenance: 'spec-sheet',
  }),
  craftMalt({
    id: 'skagit-munich',
    name: 'Munich Malt',
    maker: 'Skagit Valley Malting',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79,
    moisturePercent: 4,
    colorLovibond: 8,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Munich malt from Skagit County barley, for bready, toasty depth in bocks, Oktoberfest and amber ales. All values shown are class-typical, since Skagit publish no color or analysis for this malt.',
    tags: ['kilned', 'munich', 'malty', 'craft-malt', 'washington', 'local'],
    url: 'https://skagitvalleymalting.com/products/munich-malt-1',
  }),
  craftMalt({
    id: 'skagit-millet',
    name: 'Millet Malt',
    maker: 'Skagit Valley Malting',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 74,
    moisturePercent: 6,
    colorLovibond: 1.8,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Malted millet for gluten-free beer, made on the one machine the malthouse keeps for gluten-free grain. All values shown are class-typical for pale millet malt, since Skagit list it by name only.',
    tags: ['base', 'millet', 'gluten-free', 'craft-malt', 'washington'],
    url: 'https://skagitvalleymalting.com/products/millet-malt-1',
  }),

  // ---------------------------------------------------------------------------
  // Cascadia Malts — Nine Mile Falls, Washington
  //
  // A family malthouse working grain from the McKay family land in the Grand
  // Coulee Dam region. Their whole range is on one "Product & Pricing" page
  // (`cascadiamalts.com/new-page-1`, winter/spring 2026, also published as a
  // PDF). The page gives each malt a sentence and, for most of them, a colour
  // in SRM, which carries across to °L unchanged. Only Voodoo Pale SWW Wheat
  // publishes more: 87.3 % extract and 165 °L DP. Every other extract,
  // moisture and DP figure is class-typical, so the whole set is
  // `representative`.
  //
  // Where they give a colour range (Cascadia Gold 3–6, Light Munich 10–12),
  // the row uses the midpoint. Chit Malt and Lacey Distillers Malt have no
  // colour on the page ("CoA and tasting notes pending" for Lacey), so their
  // colours are class-typical too.
  //
  // Voodoo's 87.3 % is stored as published. It is over the 85 % that barley
  // reaches, and it is wheat: no husk, so more extract per kilogram, which is
  // why the extract ceiling in craft-consistency.test.ts is higher for the
  // `wheat` malt class. The chit row sits at the class-typical 50 % extract
  // and is named in that file's CHIT_MALTS.
  //
  // Premium Brewers Malt and Lacey Distillers Malt are both six-row, per the
  // page. The page is a family listing, so no row carries a `url`.
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'cascadia-european-style-pilsner',
    name: 'European Style Pilsner',
    maker: 'Cascadia Malts',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 1.5,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Claymore barley malted on the low side of modification for a continental pilsner: grainy, with a floral honeysuckle note, at 1.5 °L. A step mash suits it. Color is theirs; extract, moisture and diastatic power are class-typical.',
    tags: ['base', 'pilsner', 'lager', 'craft-malt', 'washington', 'claymore'],
  }),
  craftMalt({
    id: 'cascadia-claymore-pilsner',
    name: 'Claymore Pilsner',
    maker: 'Cascadia Malts',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 1.7,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Their fully modified pilsner and best seller: sweet and floral, with a light bread note, low beta-glucan and 1.7 °L color. It took silver at the 2025 Craft Malt Cup. Color is theirs; the other values are class-typical.',
    tags: ['base', 'pilsner', 'lager', 'craft-malt', 'washington', 'claymore'],
  }),
  craftMalt({
    id: 'cascadia-claymore-pale',
    name: 'Claymore Pale',
    maker: 'Cascadia Malts',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4,
    colorLovibond: 2.5,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Claymore barley kilned a little harder than the pilsner, which adds a toasty edge and drives off DMS precursors. A clean 2.5 °L base for pale ales and IPAs. Color is theirs; the other values are class-typical.',
    tags: ['base', 'pale', 'craft-malt', 'washington', 'claymore'],
  }),
  craftMalt({
    id: 'cascadia-gold',
    name: 'Cascadia Gold',
    maker: 'Cascadia Malts',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 3.5,
    colorLovibond: 4.5,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Their answer to Maris Otter and Golden Promise, kilned for a richer, biscuity English pale at 3 to 6 °L. Use it as the whole base of a bitter or an English-leaning pale ale. Color is theirs (midpoint of their range); the other values are class-typical.',
    tags: ['base', 'pale-ale', 'english', 'craft-malt', 'washington'],
  }),
  craftMalt({
    id: 'cascadia-premium-brewers-malt',
    name: 'Premium Brewers Malt',
    maker: 'Cascadia Malts',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 5,
    diastaticPowerLintner: 140,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A fully modified six-row malt with a lot of enzyme and FAN and a quiet flavor, sold as the budget base. Good for adjunct-heavy grists and dry, well-attenuated beers. Color is theirs; extract, moisture and diastatic power are six-row class-typical.',
    tags: ['base', 'six-row', 'high-enzyme', 'craft-malt', 'washington'],
  }),
  craftMalt({
    id: 'cascadia-claymore-light-munich',
    name: 'Claymore Light Munich',
    maker: 'Cascadia Malts',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4,
    colorLovibond: 11,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'A light Munich with a lingering sweetness and graham cracker and toasted biscuit notes. It still converts itself, so it can go well past 20 percent in an Oktoberfest or bock. Color is theirs (midpoint of 10 to 12); the other values are class-typical.',
    tags: ['kilned', 'munich', 'malty', 'craft-malt', 'washington', 'claymore'],
  }),
  craftMalt({
    id: 'cascadia-honeygold-10',
    name: 'HoneyGold 10',
    maker: 'Cascadia Malts',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 76,
    moisturePercent: 4.5,
    colorLovibond: 10.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'A pale honey malt, about 30 percent crystallized, that adds strong honey sweetness and some body while keeping the color near 11 °L. Five to fifteen percent in a blonde, a pale ale or a hazy IPA. Color is theirs; the other values are class-typical.',
    tags: ['crystal', 'honey', 'sweet', 'craft-malt', 'washington'],
  }),
  craftMalt({
    // Class-typical chit extract, below the 60 % floor; named in CHIT_MALTS.
    id: 'cascadia-chit',
    name: 'Chit Malt',
    maker: 'Cascadia Malts',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 50,
    moisturePercent: 5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 15,
    needsMashing: true,
    description:
      'Deliberately under-modified and barely kilned, for foam, head retention and body without changing flavor or color. Use 5 to 15 percent alongside a full base malt. They publish no figures, so all values shown are class-typical for chit malt.',
    tags: ['base', 'chit', 'foam', 'body', 'craft-malt', 'washington'],
  }),
  craftMalt({
    id: 'cascadia-lacey-distillers',
    name: 'Lacey Distillers Malt',
    maker: 'Cascadia Malts',
    type: 'base',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 4,
    diastaticPowerLintner: 100,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A Vienna-style malt from a six-row variety, built for distillers who want grain flavor as well as conversion. It took bronze at the 2025 Craft Malt Awards. Their analysis is still pending, so all values shown are class-typical.',
    tags: ['base', 'vienna', 'distilling', 'six-row', 'craft-malt', 'washington'],
  }),
  craftMalt({
    id: 'cascadia-voodoo-pale-wheat',
    name: 'Voodoo Pale SWW Wheat',
    maker: 'Cascadia Malts',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 87.3,
    moisturePercent: 5,
    colorLovibond: 2.2,
    diastaticPowerLintner: 165,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Soft white wheat malt with a sweet bread flavor, very low beta-glucan and a lot of enzyme (165 °Lintner published). Suits wheat beers and hazy ales, and it can carry a large share of the grist. Color, extract (87.3 percent) and diastatic power are theirs; moisture is class-typical.',
    tags: ['base', 'wheat', 'soft-white-wheat', 'high-enzyme', 'craft-malt', 'washington'],
  }),
];
